/**
 * `wideOpenSuppressGate` — production 検知経路で raw 2D MAR の上限ゲートから `suppressFire` flag を
 * 組み立てる (F52 lever B / ADR 0014)。
 *
 * 意図的な大開口の保持 (= あくび・ストレッチ等を続ける状態) は Bounded context の
 * 「本人が気づかず口が開く状態」 に該当せず通知対象外 (2026-06-12 grilling、 CONTEXT.md §open-hold)。
 * しかし大開口は effectiveMar が飽和帯に入る最も発火しやすい入力で、 §F52 Q2 で「初回発火は正規経路
 * (VAD latch 明け + schmitt 3s) のため baseline 系施策では原理的に消えない」 と確定した — 唯一の
 * 弁別シグナルが raw MAR の絶対振幅で、 本 gate がその上限を引く。
 *
 * 出力 (`suppressFire`) は smile gate と OR 合流して detection-machine.step() の **開口判定 (B 層)
 * リセット要因** として消費される (issue #283 と同セマンティクス = ceiling 超過の間 `openSince` を
 * リセットし続け sustain を積ませない)。 本関数は wide-open か否かの boolean を返すだけ。
 *
 * 採用根拠 (§F54 / issue #309 / report corpus/reports/2026-06-16T03-55-22.425Z): post-B1b corpus
 * (422 trial、 open-hold を n=3→20 に拡張) の Pareto 再走で、 旧 ceiling 0.8 が **open-hold FP 16/20**
 * を出していたことが判明 (tiny-n n=3 の「0/3 偽の完璧」 の下に隠れていた)。 ceiling を 0.41 に下げる
 * fine sweep [0.37, 0.45] は完全平坦プラトーで open-hold 16/20→0/20 (+16) + yawn +1 を回復し、 TP
 * 35/40 / TP-oblique 36/40 / 全 sequence-retention / 全 idle 系を 1 trial も退行させない (退行 trial
 * ID が canonical と完全一致 = ceiling は TP を 1 件も触らない)。
 *
 * `WIDE_OPEN_CEILING = 0.41` の根拠 (post-B1b corpus の peak raw MAR 真の分布、 §F54):
 *   - 発火期待 (retained open): TP max 0.17 / TP-oblique retained < 0.37 (0.30 で初めて TP-oblique −2)
 *   - 非発火 open-hold:        peak raw 2D MAR が **0.45–1.2 に分布** (旧推定の min 1.16 は n=3 単一
 *     条件の artifact = 誤り。 実際は中振幅 16 件が 0.45–0.8 帯に乗り旧 ceiling 0.8 が取りこぼしていた)
 *   ~0.41 は TP-oblique retained 上端 (<0.37) と open-hold 下端 (0.45) の間でクリーン分離する。 raw 2D
 *   MAR は lipGap/mouthWidth の無次元比のため顔サイズ・カメラ距離に概ね不変だが、 **F54 の evidence は
 *   本人・単一環境** (#306 B1b) = 他人/他条件の「自然な大きめの本物開口」 を誤抑止しないかは保証外
 *   (= overfit リスク、 ADR 0014 §Update が文書化する驚き / feedback bad 率 + snapshot で監視)。
 *
 * hysteresis / rolling median は持たない: B 層 reset は 1 frame 超過で `openSince` を消す安全側
 * (= 非発火期待側に倒れる) で、 ceiling 付近の揺れは sustain を妨げる方向にしか働かない。
 *
 * eval substrate の twin は `features/eval/lib/transforms/wide-open-suppress-v1.ts` (独立実装、
 * #236 KTD1)。 ceiling を変えるときは twin の DEFAULT_CEILING と dispatcher.test.ts の parity を
 * 同時に確認すること。
 */

/** 「意図的な大開口」 とみなす raw 2D MAR の上限。 twin / test から参照するため export する。 */
export const WIDE_OPEN_CEILING = 0.41;

export function wideOpenSuppressGate(input: {
  /** 現 frame の raw 2D MAR (= `AnalyzeFrameResult.mar`、 純粋幾何)。 */
  mar: number;
  /** face-loss frame は mar が無効値のため判定しない。 */
  faceDetected: boolean;
}): boolean {
  return input.faceDetected && Number.isFinite(input.mar) && input.mar > WIDE_OPEN_CEILING;
}
